import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, Frown } from 'lucide-react';

/**
 * Renders a standard 404 "Page Not Found" error screen.
 * This component is visually robust and centered for a clean user experience.
 * @param {object} props - Component props.
 * @param {boolean} [props.isOffline=false] - Optional prop to switch content to an "Offline" message.
 */
const NotFound = ({ isOffline = false }) => {
    const navigate = useNavigate();
    
    // Determine content based on the error type (404 vs Offline)
    const title = isOffline ? 'Offline' : '404';
    const heading = isOffline ? 'No Internet Connection' : 'Page Not Found';
    const message = isOffline 
        ? "It looks like you're offline. Please check your internet connection and try again."
        : "The page you're looking for doesn't exist or has been moved.";

    return (
        <div className="flex min-h-screen items-center justify-center p-4 bg-gray-50 font-inter">
            <div className="max-w-xl w-full text-center bg-white p-8 md:p-12 rounded-2xl shadow-xl border border-gray-100 transition-transform hover:shadow-2xl hover:scale-[1.01] duration-300">
                
                {/* Icon/Visual Header */}
                <div className="mb-6">
                    <Frown className="w-16 h-16 mx-auto text-blue-600/70 animate-bounce-slow" />
                </div>
                
                {/* Title */}
                <h1 className="text-8xl font-extrabold text-blue-600 mb-4 tracking-tight">
                    {title}
                </h1>
                
                {/* Subheading */}
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                    {heading}
                </h2>
                
                {/* Message */}
                <p className="text-gray-600 text-lg mb-8">
                    {message}
                </p>
                
                {/* Call to Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                    
                    {/* Go Back Button */}
                    <button 
                        onClick={() => navigate(-1)}
                        className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-medium shadow-md hover:bg-gray-200 transition-colors duration-200"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Go Back
                    </button>
                    
                    {/* Go Home Button */}
                    <button 
                        onClick={() => navigate('/')}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-medium shadow-lg shadow-blue-500/50 hover:bg-blue-700 transition-colors duration-200"
                    >
                        <Home className="w-5 h-5" />
                        Go to Homepage
                    </button>
                </div>
                
                {/* Footer Tip */}
                <div className="mt-8 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                        Need immediate help? Check the site navigation or contact support if the issue persists.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NotFound;